/**
 * Open-by-path bar for Document Studio: type any Firestore document path and
 * load it into the editor. As you type, a web-search-style autocomplete offers
 * recommendations — live document ids for the segment being typed plus curated
 * collection/document names from the app's path registry. Validation and load
 * errors are surfaced inline.
 */

import { KeyboardEvent, useState } from "react";
import { Button } from "risa-oasis-ui_v2";
import {
  PathSuggestion,
  usePathSuggestions,
} from "../hooks/usePathSuggestions";

interface PathBarProps {
  onOpen: (path: string) => void;
  onNew: () => void;
  onClearError: () => void;
  loading: boolean;
  error: string | null;
}

const PLACEHOLDER = "Document path, e.g. configurations/rpa";

const PathBar = ({
  onOpen,
  onNew,
  onClearError,
  loading,
  error,
}: PathBarProps) => {
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const suggestions = usePathSuggestions(value);

  const canOpen = value.trim() !== "" && !loading;
  const showDropdown = open && suggestions.length > 0;

  const clearErrorOnce = () => {
    if (error) {
      onClearError();
    }
  };

  const changeValue = (next: string) => {
    setValue(next);
    setHighlight(-1);
    setOpen(true);
    clearErrorOnce();
  };

  const submit = () => {
    if (canOpen) {
      setOpen(false);
      onOpen(value);
    }
  };

  const applySuggestion = (suggestion: PathSuggestion) => {
    clearErrorOnce();
    if (suggestion.kind === "collection") {
      // Drill in: keep the dropdown open so the next segment can be suggested.
      setValue(`${suggestion.value}/`);
      setHighlight(-1);
      setOpen(true);
    } else {
      setValue(suggestion.value);
      setOpen(false);
      onOpen(suggestion.value);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (showDropdown && highlight >= 0 && suggestions[highlight]) {
        applySuggestion(suggestions[highlight]);
      } else {
        submit();
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={(event) => changeValue(event.target.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
            placeholder={PLACEHOLDER}
            spellCheck={false}
            autoComplete="off"
            className="h-10 w-full rounded-md border border-primaryGray-15 px-3 font-mono text-sm outline-none focus:border-tertiaryBlue-4"
          />
          {showDropdown && (
            <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-primaryGray-15 bg-white py-1 shadow-lg">
              {suggestions.map((suggestion, index) => (
                <li key={`${suggestion.source}:${suggestion.value}`}>
                  <button
                    type="button"
                    // Keep input focused so onBlur does not close before onClick.
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => applySuggestion(suggestion)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                      index === highlight
                        ? "bg-tertiaryBlue-12"
                        : "hover:bg-primaryGray-16"
                    }`}
                  >
                    <span className="shrink-0 font-mono text-sm text-primaryGray-2">
                      {suggestion.label}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-tiny text-primaryGray-8">
                      {suggestion.detail}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          disabled={!canOpen}
          onClick={submit}
          buttonType="primary"
          size="medium"
        >
          {loading ? "Opening..." : "Open"}
        </Button>
        <Button
          disabled={false}
          onClick={onNew}
          buttonType="secondary"
          size="medium"
        >
          Add New
        </Button>
      </div>
      {error && (
        <span className="text-tiny font-medium text-tertiaryRed-4">
          {error}
        </span>
      )}
    </div>
  );
};

export default PathBar;
